import Class from '@/Class';
import { ICustomSingleDropDown, ICustomMultiDropDown, ICustomDropDownItemDTO } from '#/DefaultViewExtensionInterfaces';
import Utilities from '@/Utility/Utilities';

export default class CustomDropDownHelper<T extends (ICustomSingleDropDown | ICustomMultiDropDown)> extends Class  {
    private readonly externalObservable: KnockoutObservable<string>;
    private readonly internalObservable: KnockoutObservable<string>;

    public constructor(private readonly viewModel: T, private readonly dto: KnockoutObservable<string>) {
        super();
        this.externalObservable = this.observableOrDefault(this.dto, null);
        this.internalObservable = this.observableOrDefault('[]');
    }
    
    /**
     * Writes to dto
     * @param items
     * @param selected
     */
    private updateSelection(items: ICustomDropDownItemDTO[], selected: string | string[]): void {
        if (!Utilities.isNullOrUndefined(selected) && !_.isArray(selected))
            selected = [selected];

        for (const item of items) {
            item.IsSelected = selected.indexOf(item.Identifier) > -1;
        }
    }

    /**
     * Parses and return the items
     */
    private getItems(): ICustomDropDownItemDTO[] {
        const json = this.internalObservable();
        if (!json)
            return [];

        const parsed = JSON.parse(json) as ICustomDropDownItemDTO[];
        return parsed || [];
    }

    /**
     * Creates data computed
     */
    public createData(): KnockoutComputed<ICustomDropDownItemDTO[]> {
        return this.defineComputed(() => {
            const items = this.getItems();
            return items;
        });
    }

    /**
     * Creates value computed
     */
    public createValue<T1 extends (string | string[])>(isArray: boolean): KnockoutComputed<T1> {
        /**
         * Computed creates a subscription to internal observable
         * it has to be created at first, so "write" immediately fires "read"
         * (because "write" writes to internal observable and "read" has a dependency on it)
         */
        const computed = this.defineComputed({
            read: () => {
                const items = this.getItems();

                const selectedIdentifier = _.filter(items, x => x.IsSelected).map(x => x.Identifier);
                if (!isArray)
                    return selectedIdentifier[0] || '';
                else
                    return selectedIdentifier;
            },
            write: (value: T extends ICustomSingleDropDown ? string : string[]) => {
                this.updateSelection(this.viewModel.data(), value);
                const json = JSON.stringify(this.viewModel.data());
                this.internalObservable(json);
            }
        });

        // Create subscriptions to sync external and internal observable.
        // External observable changes (from generic object) calls uilistener.
        // In uilistener the observables of this control must contain the current value!
        this.syncExternalAndInternalObservables();

        return computed as KnockoutComputed<T1>;
    }

    /**
     * synchronizes external and internal observable
     */
    private syncExternalAndInternalObservables() {
        let isSync = false;

        // Subscribe to external and write to internal
        this.defineSubscription(this.externalObservable,
            (value) => {
                if (isSync)
                    return;
                isSync = true;
                this.internalObservable(value);
                isSync = false;
            })(this.externalObservable()); // execute immediately

        // Subscribe to internal and write to external
        this.defineSubscription(this.internalObservable,
            (value) => {
                if (isSync)
                    return;
                isSync = true;
                this.externalObservable(value);
                isSync = false;
            });
    }
}